import React, { useEffect, useState } from 'react';
import cm from 'classnames';
const ERR_ID = 'editor-render.tsx';

import { useAppSelector, useAppDispatch } from 'store/store-hooks';
import { IntroKeys } from 'components/app/intro/intro-keys';
import { Path, useAppNavigator } from 'components/_hooks/use-router';
import { fetchFinalcuts, setStoryWithFinalcut } from 'components/app/_mediator/app-data.slice';
import { selectSbInitialization } from 'components/app/_mediator/app.slice';
import { setSelectedFinalcut } from 'components/app/projects/_mediator/projects.slice';
import { aggregator } from 'components/app/editor/_mediator/monitor/monitor-req.slice';
import { submitRender } from 'components/app/editor/_mediator/editor.slice';
import { useRenderValidation } from './use-render-validation';

import { Loader } from 'components/UI/loader/loader';
import { Tooltip } from 'components/UI/tooltip/tooltip';
import { AppButton } from 'components/app/app-components/app-button/app-button';
import styles from './_editor-render.module.scss';

export const EditorRender: React.FC = () => {
  const { navigateTo } = useAppNavigator();
  const { pending, buffer } = useAppSelector((state) => state.monitorReq);
  const isSbInitialized = useAppSelector(selectSbInitialization);
  const selectedStory = useAppSelector((state) => state.projects.selectedStory);
  const errorMessage = useRenderValidation();
  const dispatch = useAppDispatch();
  const [listenSubmit, setListenSubmit] = useState(false);

  const handleSubmit = () => {
    if (errorMessage || listenSubmit) return;
    dispatch(aggregator(selectedStory));
    setListenSubmit(true);
  };

  const renderSubmit = async (storyId: StoryId) => {
    dispatch(submitRender(storyId))
      .unwrap()
      .then(async (renderingFcId) => {
        if (!renderingFcId) {
          console.error(`[${ERR_ID}] renderSubmit: renderingFcId is not created`);
          return;
        }
        await dispatch(fetchFinalcuts(storyId));
        dispatch(setSelectedFinalcut(renderingFcId));
        dispatch(setStoryWithFinalcut({ storyId, withFinalcuts: true }));
        navigateTo(Path.finalcuts);
      })
      .finally(() => setListenSubmit(false));
  };

  useEffect(() => {
    if (!selectedStory) return;
    if (!listenSubmit) return;
    if (pending || buffer) return;
    renderSubmit(selectedStory);
  }, [pending, buffer, listenSubmit]);

  return (
    <Tooltip tip={errorMessage ?? ''} showTooltip={!!errorMessage} position="top">
      <AppButton
        className={cm(styles.RenderBtn, {
          [styles['RenderBtn--disabled']]: !isSbInitialized,
          [styles['RenderBtn--error']]: isSbInitialized && errorMessage,
        })}
        onClick={handleSubmit}
        data-intro={IntroKeys.sbRender}
        tip="When you are finished storyboarding, render a Final Cut."
        tipPosition="left"
      >
        {listenSubmit ? <Loader className={styles.RenderBtn__loader} /> : 'Render'}
      </AppButton>
    </Tooltip>
  );
};
